import { ModalDialog } from '@/scripts/theme/modal-dialog'
import { TsDOM as q } from '@/scripts/core/TsDOM'
import { geocodeAddress, locationGeocodeQuery } from '@/scripts/theme/geocode-cache'
import type { LocationData } from '@/scripts/theme/location-list'

// Minimal local typings for the Google Maps JS API.
type GMap = {
	fitBounds: (bounds: GLatLngBounds, padding?: number) => void
	panTo: (latLng: GLatLngLiteral) => void
	setCenter: (latLng: GLatLngLiteral) => void
	setZoom: (zoom: number) => void
	getZoom: () => number | undefined
	addListener: (event: string, handler: () => void) => void
}
type GLatLngLiteral = { lat: number; lng: number }
type GLatLngBounds = {
	extend: (latLng: GLatLngLiteral) => void
	isEmpty: () => boolean
}
type GMarker = {
	setMap: (map: GMap | null) => void
	setAnimation: (animation: number | null) => void
	setVisible: (visible: boolean) => void
	setIcon: (icon: Record<string, unknown> | string | null) => void
	addListener: (event: string, handler: () => void) => void
}
type GInfoWindow = {
	setContent: (content: string | HTMLElement) => void
	open: (opts: { anchor?: GMarker; map?: GMap }) => void
	close: () => void
}
type GMaps = {
	Map: new (el: HTMLElement, opts: Record<string, unknown>) => GMap
	Marker: new (opts: Record<string, unknown>) => GMarker
	InfoWindow: new (opts?: Record<string, unknown>) => GInfoWindow
	LatLngBounds: new () => GLatLngBounds
	Size: new (w: number, h: number) => unknown
	Point: new (x: number, y: number) => unknown
	Animation: { BOUNCE: number; DROP: number }
}

declare global {
	interface Window {
		google?: { maps: GMaps }
		[k: string]: unknown
	}
}

let scriptPromise: Promise<GMaps> | undefined

function loadGoogleMaps(key: string): Promise<GMaps> {
	if (window.google?.maps) return Promise.resolve(window.google.maps)
	if (scriptPromise) return scriptPromise

	scriptPromise = new Promise<GMaps>((resolve, reject) => {
		const callbackName = `__locationMapInit_${Date.now()}`
		;(window as Record<string, unknown>)[callbackName] = () => {
			const maps = window.google?.maps
			if (maps) resolve(maps)
			else reject(new Error('Google Maps loaded but window.google.maps is missing'))
			delete (window as Record<string, unknown>)[callbackName]
		}

		const script = document.createElement('script')
		script.src = `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(key)}&loading=async&callback=${callbackName}`
		script.async = true
		script.defer = true
		script.onerror = () => {
			reject(new Error('Failed to load Google Maps JS'))
			scriptPromise = undefined
		}
		document.head.appendChild(script)
	})

	return scriptPromise
}

export class LocationMapModal extends ModalDialog {
	static override htmlSelector = 'location-map-modal'

	private locations: LocationData[] = []
	private map?: GMap
	private markers = new Map<string, GMarker>()
	private infoWindow?: GInfoWindow
	private mapContainer?: HTMLElement
	private listContainer?: HTMLOListElement
	private tabButtons: HTMLButtonElement[] = []
	private initStarted = false
	private listRendered = false
	private coordsPromise?: Promise<void>
	private activeCity?: string
	private activeMarkerHandle?: string

	// Markers scale with zoom so they don't overwhelm the map when zoomed
	// out: MIN at zoom <= 13, MAX at zoom >= 18, linear between. The active
	// (clicked) marker is drawn at the zoom size * ACTIVE_SCALE.
	private static readonly MARKER_MIN_SIZE = 24
	private static readonly MARKER_MAX_SIZE = 72
	private static readonly MARKER_ACTIVE_SCALE = 1.4

	// Street-level zoom used when only one store is on the map (or one city is
	// filtered). fitBounds on a single point zooms to Google's max, which reads
	// as "broken" — e.g. a lone Paris store filling the frame at building level.
	private static readonly SINGLE_LOCATION_ZOOM = 15

	// Cached brand colour for the fallback pin, read from the component's CSS
	// custom properties once the element is connected.
	private defaultPinColor?: string

	constructor() {
		super()
		this.locations = this.parseLocations()
	}

	override getHtmlSelector() {
		return LocationMapModal.htmlSelector
	}

	private parseLocations(): LocationData[] {
		const raw = q.oa(this, 'data-locations')
		if (!raw) return []
		try {
			return JSON.parse(raw) as LocationData[]
		} catch (err) {
			console.warn('LocationMapModal: failed to parse locations JSON', err)
			return []
		}
	}

	override show(opener: HTMLElement) {
		super.show(opener)
		this.mapContainer = this.mapContainer ?? q.os<HTMLElement>('[data-location-list-map]', this)
		this.listContainer = this.listContainer ?? q.os<HTMLOListElement>('[data-location-list-modal-list]', this)

		if (this.tabButtons.length === 0) {
			this.tabButtons = Array.from(
				q.ol<HTMLButtonElement>('[data-location-list-modal-tab]', this) ?? []
			)
			this.tabButtons.forEach((btn) =>
				btn.addEventListener('click', () => {
					const city = btn.dataset.locationListModalTab
					if (city) this.applyCityFilter(city)
				})
			)
		}

		// Adopt the location list's currently-selected city each time the map
		// opens, so opening the map while e.g. "Paris" is filtered lands on
		// Paris. Falls back to whichever tab the modal already has selected.
		const desiredCity = this.resolveDesiredCity()
		if (desiredCity !== this.activeCity) {
			this.activeCity = desiredCity
			this.syncTabSelection(desiredCity)
			this.renderInitialList()
			// No-op on the very first open (markers don't exist yet — initMap
			// builds them filtered by activeCity); refilters on re-open.
			this.applyMarkerFilter(desiredCity)
		} else if (!this.listRendered) {
			this.renderInitialList()
		}

		void this.initMap()
	}

	// The city selected in the associated <location-list>, falling back to the
	// modal's own selected tab. Both live in the same section and share the
	// modal id, so we match the list by the id it points at.
	private resolveDesiredCity(): string | undefined {
		return this.listActiveCity() ?? this.selectedModalTabCity()
	}

	private listActiveCity(): string | undefined {
		if (!this.id) return undefined
		const listEl = document.querySelector(`location-list[data-modal-id="${CSS.escape(this.id)}"]`)
		const activeTab = listEl?.querySelector<HTMLElement>(
			'[data-location-list-tab][aria-selected="true"]'
		)
		return activeTab?.dataset.locationListTab || undefined
	}

	private selectedModalTabCity(): string | undefined {
		const selected = this.tabButtons.find((b) => b.getAttribute('aria-selected') === 'true')
		return selected?.dataset.locationListModalTab
	}

	private syncTabSelection(city?: string) {
		this.tabButtons.forEach((b) => {
			b.setAttribute('aria-selected', String(!!city && b.dataset.locationListModalTab === city))
		})
	}

	private renderInitialList() {
		if (!this.listContainer) return
		this.listContainer.innerHTML = this.visibleLocations()
			.map((loc) => this.renderListItem(loc))
			.join('')
		this.listRendered = true
	}

	renderSortedList(sorted: Array<{ loc: LocationData; distance: number }>) {
		if (!this.listContainer) {
			this.listContainer = q.os<HTMLOListElement>('[data-location-list-modal-list]', this)
		}
		if (!this.listContainer) return
		const visible = this.activeCity
			? sorted.filter(({ loc }) => loc.cityHandle === this.activeCity)
			: sorted
		this.listContainer.innerHTML = visible
			.map(({ loc, distance }) => this.renderListItem(loc, distance))
			.join('')
		this.listRendered = true
	}

	private visibleLocations(): LocationData[] {
		if (!this.activeCity) return this.locations
		return this.locations.filter((l) => l.cityHandle === this.activeCity)
	}

	private renderListItem(loc: LocationData, distanceKm?: number): string {
		const distanceLabel = distanceKm !== undefined ? formatDistance(distanceKm) : ''
		const iconMarkup = loc.iconUrl
			? `<img class="location-map-modal__list-icon" src="${escapeHtml(loc.iconUrl)}" alt="" width="24" height="24" loading="lazy">`
			: `<span class="location-map-modal__list-icon location-map-modal__list-icon--default" aria-hidden="true"></span>`
		return `
			<li class="location-map-modal__list-item" data-location-list-modal-item data-handle="${escapeHtml(loc.handle)}">
				<button type="button" class="location-map-modal__list-button">
					${iconMarkup}
					<span class="location-map-modal__list-name">${escapeHtml(loc.name)}</span>
					${distanceLabel ? `<span class="location-map-modal__list-distance">${distanceLabel}</span>` : ''}
				</button>
			</li>
		`.trim()
	}

	private applyCityFilter(city: string) {
		this.activeCity = city
		this.syncTabSelection(city)
		this.renderInitialList()
		this.applyMarkerFilter(city)
	}

	// Hide/show markers for the given city (undefined = show all) and refit the
	// map to what's visible. No-op until the map + markers exist.
	private applyMarkerFilter(city?: string) {
		if (!this.map || !window.google) return
		const visiblePositions: GLatLngLiteral[] = []
		this.locations.forEach((loc) => {
			const marker = this.markers.get(loc.handle)
			if (!marker) return
			const match = !city || loc.cityHandle === city
			marker.setVisible(match)
			if (match && typeof loc.lat === 'number' && typeof loc.lng === 'number') {
				visiblePositions.push({ lat: loc.lat, lng: loc.lng })
			}
		})
		this.fitToPositions(visiblePositions)
	}

	private resolveAllCoords(googleKey: string): Promise<void> {
		if (this.coordsPromise) return this.coordsPromise
		this.coordsPromise = Promise.all(
			this.locations.map(async (loc) => {
				if (typeof loc.lat === 'number' && typeof loc.lng === 'number') return
				const query = locationGeocodeQuery(loc.postcode, loc.city)
				if (!query) return
				const coords = await geocodeAddress(query, googleKey)
				if (coords) {
					loc.lat = coords.lat
					loc.lng = coords.lng
				}
			})
		).then(() => {
			const resolved = this.locations.filter(
				(l) => typeof l.lat === 'number' && typeof l.lng === 'number'
			)
			const failed = this.locations.filter(
				(l) => typeof l.lat !== 'number' || typeof l.lng !== 'number'
			)
			console.log(
				`[location-map] geocoding summary: ${resolved.length} placed, ${failed.length} skipped`
			)
			if (failed.length) {
				console.warn(
					'[location-map] these locations were NOT placed on the map (no postcode, or geocoding returned no result):',
					failed.map((l) => ({
						handle: l.handle,
						name: l.name,
						city: l.city,
						postcode: l.postcode || '(empty)',
					}))
				)
			}
		})
		return this.coordsPromise
	}

	private async initMap() {
		if (this.initStarted || !this.mapContainer) return
		const key = q.oa(this, 'data-google-key')
		if (!key) {
			console.warn('LocationMapModal: no Google Maps API key configured')
			return
		}
		if (this.locations.length === 0) return
		this.initStarted = true

		this.setMapStatus('Loading map…')

		let maps: GMaps
		try {
			const [loadedMaps] = await Promise.all([loadGoogleMaps(key), this.resolveAllCoords(key)])
			maps = loadedMaps
		} catch (err) {
			console.warn('LocationMapModal: Google Maps failed to load', err)
			this.initStarted = false
			this.setMapStatus('Map failed to load. Please try again.')
			return
		}

		const positioned = this.locations.filter(
			(l): l is LocationData & { lat: number; lng: number } =>
				typeof l.lat === 'number' && typeof l.lng === 'number'
		)
		if (positioned.length === 0) {
			this.setMapStatus('We couldn’t place these stores on the map.')
			return
		}

		this.clearMapStatus()

		const styles = this.parseSnazzyStyle()
		const center = computeCentroid(positioned)
		this.map = new maps.Map(this.mapContainer, {
			center,
			zoom: 14,
			// Prevents fitBounds from zooming out below street level when
			// markers are spread across a city — keeps the map legible.
			minZoom: 13,
			styles,
			mapTypeControl: false,
			streetViewControl: false,
			fullscreenControl: false,
			clickableIcons: false,
		})

		this.infoWindow = new maps.InfoWindow({ maxWidth: 280 })

		const baseSize = this.markerSizeForZoom(this.map.getZoom() ?? 14)
		const visiblePositions: GLatLngLiteral[] = []
		positioned.forEach((loc) => {
			const markerOptions: Record<string, unknown> = {
				position: { lat: loc.lat, lng: loc.lng },
				map: this.map,
				title: loc.name,
			}
			const baseIcon = this.buildMarkerIcon(loc, maps, baseSize)
			if (baseIcon) markerOptions.icon = baseIcon
			const marker = new maps.Marker(markerOptions)
			if (this.activeCity && loc.cityHandle !== this.activeCity) {
				marker.setVisible(false)
			}
			marker.addListener('click', () => {
				this.scaleActiveMarker(loc.handle)
				this.openInfoWindow(loc, marker)
			})
			this.markers.set(loc.handle, marker)
			if (!this.activeCity || loc.cityHandle === this.activeCity) {
				visiblePositions.push({ lat: loc.lat, lng: loc.lng })
			}
		})

		// Re-scale every marker whenever the user zooms.
		this.map.addListener('zoom_changed', () => this.applyZoomSizing())

		this.fitToPositions(visiblePositions)
	}

	// Frame a set of points. A single point can't produce meaningful bounds —
	// fitBounds would zoom to Google's max — so centre on it at a fixed
	// street-level zoom instead. Two or more points use fitBounds as normal.
	private fitToPositions(positions: GLatLngLiteral[]) {
		if (!this.map || !window.google || positions.length === 0) return
		if (positions.length === 1) {
			this.map.setCenter(positions[0])
			this.map.setZoom(LocationMapModal.SINGLE_LOCATION_ZOOM)
			return
		}
		const bounds = new window.google.maps.LatLngBounds()
		positions.forEach((p) => bounds.extend(p))
		if (!bounds.isEmpty()) this.map.fitBounds(bounds, 32)
	}

	// Map zoom -> marker pixel size. Linear from MIN (zoom <= 13) to MAX
	// (zoom >= 18).
	private markerSizeForZoom(zoom: number): number {
		const min = LocationMapModal.MARKER_MIN_SIZE
		const max = LocationMapModal.MARKER_MAX_SIZE
		const t = Math.max(0, Math.min(1, (zoom - 13) / 5))
		return Math.round(min + (max - min) * t)
	}

	private applyZoomSizing() {
		const maps = window.google?.maps
		if (!maps || !this.map) return
		const zoom = this.map.getZoom()
		if (typeof zoom !== 'number') return
		const base = this.markerSizeForZoom(zoom)
		this.markers.forEach((marker, handle) => {
			const loc = this.locations.find((l) => l.handle === handle)
			if (!loc) return
			const size =
				handle === this.activeMarkerHandle
					? Math.round(base * LocationMapModal.MARKER_ACTIVE_SCALE)
					: base
			const icon = this.buildMarkerIcon(loc, maps, size)
			if (icon) marker.setIcon(icon)
		})
	}

	private openInfoWindow(loc: LocationData, marker: GMarker) {
		if (!this.infoWindow) return
		const container = document.createElement('div')
		container.className = 'location-map-modal__info'

		const parts: string[] = []
		parts.push(
			`<button type="button" class="location-map-modal__info-close" aria-label="Close" data-info-close>` +
				`<svg viewBox="0 0 14 14" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">` +
				`<path d="M1 1l12 12M13 1L1 13" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>` +
				`</svg>` +
				`</button>`
		)
		if (loc.imageUrl) {
			parts.push(
				`<img class="location-map-modal__info-image" src="${escapeHtml(loc.imageUrl)}" alt="" loading="lazy">`
			)
		}
		parts.push(`<div class="location-map-modal__info-body">`)
		parts.push(`<h4 class="location-map-modal__info-name">${escapeHtml(loc.name)}</h4>`)
		if (loc.address) {
			parts.push(
				`<p class="location-map-modal__info-line">${escapeHtml(loc.address).replace(/\n/g, '<br>')}</p>`
			)
		}
		if (loc.postcode) {
			parts.push(`<p class="location-map-modal__info-line">${escapeHtml(loc.postcode)}</p>`)
		}
		if (loc.openingDays || loc.openingHours) {
			parts.push('<p class="location-map-modal__info-line location-map-modal__info-hours">')
			if (loc.openingDays) parts.push(`<span>${escapeHtml(loc.openingDays)}</span>`)
			if (loc.openingHours) parts.push(`<span>${escapeHtml(loc.openingHours)}</span>`)
			parts.push('</p>')
		}
		if (loc.weekendDays || loc.weekendHours) {
			parts.push(
				'<p class="location-map-modal__info-line location-map-modal__info-hours location-map-modal__info-hours--weekend">'
			)
			if (loc.weekendDays) parts.push(`<span>${escapeHtml(loc.weekendDays)}</span>`)
			if (loc.weekendHours) parts.push(`<span>${escapeHtml(loc.weekendHours)}</span>`)
			parts.push('</p>')
		}
		if (loc.orderLink) {
			parts.push(
				`<a class="location-map-modal__info-link" href="${escapeHtml(loc.orderLink)}">Order here</a>`
			)
		}
		parts.push(`</div>`)
		container.innerHTML = parts.join('')

		const closeBtn = container.querySelector<HTMLButtonElement>('[data-info-close]')
		closeBtn?.addEventListener('click', () => this.infoWindow?.close())

		this.infoWindow.setContent(container)
		this.infoWindow.open({ anchor: marker, map: this.map })
	}

	private setMapStatus(message: string) {
		if (!this.mapContainer) return
		this.mapContainer.dataset.status = 'loading'
		this.mapContainer.textContent = message
	}

	private clearMapStatus() {
		if (!this.mapContainer) return
		this.mapContainer.removeAttribute('data-status')
		this.mapContainer.textContent = ''
	}

	private parseSnazzyStyle(): unknown {
		const raw = q.oa(this, 'data-snazzy-style')
		if (!raw) return undefined
		try {
			return JSON.parse(raw)
		} catch (err) {
			console.warn('LocationMapModal: invalid Snazzy style JSON', err)
			return undefined
		}
	}

	focusLocation(loc: LocationData) {
		if (!this.map || !window.google) return
		if (typeof loc.lat !== 'number' || typeof loc.lng !== 'number') return
		this.map.panTo({ lat: loc.lat, lng: loc.lng })
		this.map.setZoom(15)
		this.scaleActiveMarker(loc.handle)
	}

	private buildMarkerIcon(loc: LocationData, maps: GMaps, size: number) {
		// No merchant logo → draw a brand-coloured fallback pin rather than
		// letting Google fall back to its generic red teardrop.
		const url = loc.iconUrl || this.defaultPinDataUri()
		return {
			url,
			scaledSize: new maps.Size(size, size),
			anchor: new maps.Point(size / 2, size / 2),
		}
	}

	// Circular fallback pin as an inline SVG data URI, tinted with the brand
	// colour. Centred (like the logo markers) so it reuses the same anchor math.
	private defaultPinDataUri(): string {
		const color = this.readBrandColor()
		const svg =
			`<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">` +
			`<circle cx="12" cy="12" r="10" fill="${color}" stroke="#ffffff" stroke-width="2"/>` +
			`<circle cx="12" cy="12" r="3.5" fill="#ffffff"/>` +
			`</svg>`
		// encodeURIComponent handles '#', spaces and commas so rgb()/oklch()
		// colour values survive intact in the data URI.
		return `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`
	}

	// Pull the brand colour from the component's CSS custom properties, falling
	// back through button → foreground → a dark neutral. Cached after first read.
	private readBrandColor(): string {
		if (this.defaultPinColor) return this.defaultPinColor
		const styles = getComputedStyle(this)
		const color =
			styles.getPropertyValue('--c-button').trim() ||
			styles.getPropertyValue('--c-foreground').trim() ||
			'#1a1a1a'
		this.defaultPinColor = color
		return color
	}

	// Active marker reads as visually scaled-up vs. the others.
	// Replaces the previous BOUNCE animation per brand: no translate motion.
	private scaleActiveMarker(handle: string) {
		const maps = window.google?.maps
		if (!maps || !this.map) return

		const base = this.markerSizeForZoom(this.map.getZoom() ?? 14)
		const activeSize = Math.round(base * LocationMapModal.MARKER_ACTIVE_SCALE)

		if (this.activeMarkerHandle && this.activeMarkerHandle !== handle) {
			const prevMarker = this.markers.get(this.activeMarkerHandle)
			const prevLoc = this.locations.find((l) => l.handle === this.activeMarkerHandle)
			if (prevMarker && prevLoc) {
				const icon = this.buildMarkerIcon(prevLoc, maps, base)
				if (icon) prevMarker.setIcon(icon)
			}
		}

		const marker = this.markers.get(handle)
		const loc = this.locations.find((l) => l.handle === handle)
		if (marker && loc) {
			const icon = this.buildMarkerIcon(loc, maps, activeSize)
			if (icon) marker.setIcon(icon)
		}
		this.activeMarkerHandle = handle
	}
}

function computeCentroid(locations: Array<{ lat: number; lng: number }>): GLatLngLiteral {
	if (locations.length === 0) return { lat: 0, lng: 0 }
	const sum = locations.reduce(
		(acc, loc) => ({ lat: acc.lat + loc.lat, lng: acc.lng + loc.lng }),
		{ lat: 0, lng: 0 }
	)
	return { lat: sum.lat / locations.length, lng: sum.lng / locations.length }
}

function formatDistance(km: number): string {
	if (km < 1) return `${Math.round(km * 1000)} metres`
	if (km < 10) return `${km.toFixed(1)}km`
	return `${Math.round(km)}km`
}

function escapeHtml(input: string): string {
	return input
		.replace(/&/g, '&amp;')
		.replace(/</g, '&lt;')
		.replace(/>/g, '&gt;')
		.replace(/"/g, '&quot;')
		.replace(/'/g, '&#039;')
}
